import { emptyLabelFor as sharedEmptyLabelFor } from '@/utils/reports/chartEmptyValueConstants';

export const emptyValue = {
  job_name: 'No job name',
  primary_recruiter: 'No primary recruiter',
  primary_hm: 'No primary hiring manager',
  appl_source: 'No source',
  department: 'No department',
  root_department: 'No department',
  appl_location: 'No location',
  rejection_type: 'No rejection type',
  rejection_reason: 'No rejection reason',
  gender: 'Unknown',
  appl_credited_to: 'Not credited',
  job_location: 'No location',
  appl_contact_source: 'No source',
  rejected_by: 'No rejected by',
  rejected_stage: 'Unknown',
  contact_source: 'No source',
  contact_credited_to: 'Not credited',
  contact_location: 'No location',
  experience_bucket: 'Unknown',
};

/**
 * What an unanswered bucket reads as.
 *
 * A custom field has no emptyValue entry -- that map holds one per built-in dimension, and an
 * account's own fields are not knowable here -- so an unanswered bucket rendered as a blank row.
 * "No value" rather than "No <field name>": accounts name their fields freely, so the name does
 * not reliably read as a sentence.
 */
// The report tiles render through chartTableFlatTransformer, not this file; the two held
// near-identical copies of this logic and only one got the custom field wording. Both now read
// the same resolver.
const emptyLabelFor = (key) => emptyValue[key] ?? sharedEmptyLabelFor(key);

const getName = (input, key) => {
  if (!input) return emptyLabelFor(key);
  if (typeof input !== 'object') return input;
  return input?.name ?? input?.title ?? input?.reason ?? input?.label;
};

export function flatTransformer({
  headers,
  datas,
  g1 = [],
  g2 = [],
  dt = [],
  g1_l2 = [],
  g2_l2 = [],
  headerKey = 'dt',
  g1Key,
  g2Key,
}) {
  return datas?.map((data) => {
    return headers?.reduce((acc, header, idx) => {
      // Handle dt field
      if (header === 'dt' && typeof data[idx] === 'number') {
        acc[header] = dt[data[idx]];
        if (headerKey === 'dt') {
          acc['header'] = getName(dt[data[idx]], g1Key);
        }
        return acc;
      }

      // Handle g1 field
      if (header === 'g1' && typeof data[idx] === 'number') {
        const g1Data = g1[data[idx]];
        acc[header] = g1Data;

        if (Array.isArray(g1Data) && g1_l2?.length) {
          // Add mapped g1_l2 objects
          const g1Objects = g1Data.map((index) => g1_l2[index]);

          // Set g1 to be array of IDs from g1_l2
          acc[header] = g1Objects.map((obj) => obj.id);

          // Add objects in g1_l2 property
          acc['g1_l2'] = g1Objects;
          if (headerKey === 'g1') {
            acc['header'] = g1Data
              .map((index) => getName(g1_l2[index], g1Key))
              .join(', ');
            acc['parentHeader'] = g1Data.map((index) =>
              getName(g1_l2[index], g1Key)
            );
          }
        } else {
          if (headerKey === 'g1') {
            acc['header'] = getName(g1Data, g1Key);
            acc['parentHeader'] = getName(g1Data, g1Key);
          }
          if (!g1Data) {
            acc['emptyValue'] = emptyLabelFor(g1Key) || dt[data[idx]] || null;
            acc['parentHeader'] = emptyLabelFor(g1Key) || dt[data[idx]] || null;
          } else {
            acc['parentHeader'] = getName(g1Data, g1Key);
          }
        }
        return acc;
      }

      // Handle g2 field
      if (header === 'g2' && typeof data[idx] === 'number') {
        const g2Data = g2[data[idx]];
        acc[header] = g2Data;
        if (Array.isArray(g2Data) && g2_l2?.length) {
          const g2Objects = g2Data.map((index) => g2_l2[index]);

          acc[header] = g2Objects.map((obj) => obj.id);

          acc['g1_l2'] = g2Objects;
          if (headerKey === 'g2') {
            acc['header'] = g2Data
              .map((index) => getName(g2_l2[index], g2Key))
              .join(', ');
          }
        } else {
          if (headerKey === 'g2') {
            acc['header'] = getName(g2Data, g2Key);
          }
          if (!g2Data) {
            acc['emptyValue'] = emptyLabelFor(g2Key) || dt[data[idx]] || null;
          }
        }
        return acc;
      }

      // Handle ds field (subrows)
      if (header === 'ds' && Array.isArray(data[idx])) {
        acc['subRows'] = flatTransformer({
          headers,
          datas: data[idx],
          g1,
          g2,
          dt,
          g1_l2,
          g2_l2,
          headerKey: 'g2', // This is hardcoded
          g1Key,
          g2Key,
        });
        return acc;
      }

      // Default case
      acc[header] = data[idx];
      return acc;
    }, {});
  });
}

export const getTableData = (response, payload) => {
  const {
    chart = [],
    table = [],
    headers,
    lookups: { dt, g1_l2 = [], g1 = [], g2 = [], g2_l2 = [] } = {},
  } = response?.data || {};
  // using the new g1_l2 if present or fallback to g1

  const { group: { group_by_1, group_by_2 } = {} } = payload || {};

  const datas =
    g1_l2?.length || g1?.length || g2_l2?.length || g2?.length ? table : chart;
  const headerKey = g1_l2?.length || g1?.length ? 'g1' : 'dt';
  return flatTransformer({
    headers,
    datas,
    g1,
    g2,
    dt,
    g1_l2,
    g2_l2,
    headerKey,
    g1Key: group_by_1?.id,
    g2Key: group_by_2?.id,
  });
};
